import React, { useMemo } from 'react';
import { t } from '@/locales/strings';

/**
 * WeekDots — a 7-circle row showing which days of the current week
 * the user trained. Completed days are filled with the brand rose,
 * today is a ring, missed days are a hairline. A small label above
 * reads "THIS WEEK" so the surface is instantly scannable.
 *
 * Data model: the DB tracks streak_days (a count) and last_session_at
 * (the most recent training day). We infer the completed dates as
 * the consecutive run ending at last_session_at — i.e. dates
 * [last_session_at − (streakDays − 1) … last_session_at]. This is
 * the same inference the streak counter itself relies on, so the
 * two surfaces always agree. Future days inside the week render
 * dimly so the shape of the week still reads.
 */
const MILESTONES = [7, 14, 30, 60, 100, 200, 365];

function nextMilestone(streakDays) {
  for (const m of MILESTONES) if (m > streakDays) return m;
  return null;
}

export default function WeekDots({ streakDays = 0, lastSessionAt, lang = 'en' }) {
  const days = useMemo(() => buildWeek(streakDays, lastSessionAt), [streakDays, lastSessionAt]);
  const nextM = nextMilestone(streakDays);
  const daysToNext = nextM ? nextM - streakDays : 0;

  // Localised day initials — M T W T F S S in EN, S T Q Q S S D in PT
  // (Portuguese weekday initials starting Monday).
  const initials = lang === 'pt'
    ? ['S', 'T', 'Q', 'Q', 'S', 'S', 'D']
    : ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

  return (
    <div
      role="group"
      aria-label={t('week_dots_aria', lang)}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 4,
        margin: '4px 0 2px',
      }}
    >
      <div style={{
        font: '600 9.5px/1 var(--font-ui, Manrope)',
        letterSpacing: '0.3em',
        textTransform: 'uppercase',
        color: 'var(--ink-mute, #9c8590)',
      }}>
        {t('week_dots_eyebrow', lang)}
      </div>
      <div style={{ display: 'flex', gap: 10 }}>
        {days.map((d, i) => (
          <Dot key={i} initial={initials[i]} state={d.state} />
        ))}
      </div>
      {/* Next-milestone teaser. Renders only when a streak is active
          AND the next milestone is within 30 days — beyond that it
          reads as pressure rather than encouragement. */}
      {streakDays >= 1 && nextM && daysToNext <= 30 && (
        <div style={{
          font: '400 11px/1.3 var(--font-title, Fraunces), serif',
          fontStyle: 'italic',
          color: 'var(--ink-mute, #9c8590)',
          marginTop: 2,
          textWrap: 'balance',
        }}>
          {t('week_dots_next_milestone', lang)
            .replace('{days}', String(daysToNext))
            .replace('{milestone}', String(nextM))}
        </div>
      )}
    </div>
  );
}

function Dot({ initial, state }) {
  const filled   = state === 'done';
  const isToday  = state === 'today';
  const isFuture = state === 'future';

  const bg =
    filled   ? 'var(--primary, #c97a68)'
    : isToday ? 'transparent'
    : 'transparent';
  const border =
    filled   ? 'var(--primary, #c97a68)'
    : isToday ? 'var(--primary, #c97a68)'
    : isFuture ? 'color-mix(in srgb, var(--ink-mute, #9c8590) 20%, transparent)'
    : 'color-mix(in srgb, var(--ink-mute, #9c8590) 35%, transparent)';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
      <div
        aria-hidden="true"
        style={{
          width: 16, height: 16,
          borderRadius: '50%',
          background: bg,
          border: `1.5px ${isToday ? 'solid' : 'solid'} ${border}`,
          boxShadow: isToday ? `0 0 0 3px color-mix(in srgb, var(--primary, #c97a68) 15%, transparent)` : 'none',
          transition: 'background 280ms ease, border-color 280ms ease',
        }}
      />
      <div style={{
        font: '600 9px/1 var(--font-ui, Manrope)',
        letterSpacing: '0.08em',
        color: filled || isToday
          ? 'var(--primary-dark, #a85c4b)'
          : 'var(--ink-mute, #9c8590)',
        opacity: isFuture ? 0.5 : 1,
      }}>
        {initial}
      </div>
    </div>
  );
}

// ── Helpers ──────────────────────────────────────────────────────

// Get the local Monday of the week that contains `date`. 00:00 local.
function mondayOf(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const day = d.getDay(); // 0 = Sun, 1 = Mon, … 6 = Sat
  const offset = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + offset);
  return d;
}

function sameDay(a, b) {
  return a.getFullYear() === b.getFullYear()
    && a.getMonth() === b.getMonth()
    && a.getDate() === b.getDate();
}

/**
 * Build the 7-day array for the current week with a state per day.
 * Exported for unit-testing.
 */
export function buildWeek(streakDays, lastSessionAt, now = new Date()) {
  const monday = mondayOf(now);
  const days = [];

  // Derive the "completed" date range from streak + last session.
  // A positive streak of N ending on lastSessionAt means the N
  // consecutive days ending at lastSessionAt (inclusive) were
  // completed. An absent or unparseable lastSessionAt, or one that
  // sits in the future (clock skew / bad data), is treated as zero
  // completions so the whole week renders as "missed / today /
  // future" rather than silently painting impossible days done.
  let last = null;
  if (lastSessionAt) {
    const parsed = new Date(lastSessionAt);
    const isValid = !Number.isNaN(parsed.getTime());
    if (isValid && parsed <= now) {
      last = parsed;
      last.setHours(0, 0, 0, 0);
    }
  }
  // Negative / zero streak can't mark any day done.
  const safeStreak = Number.isFinite(streakDays) && streakDays > 0 ? streakDays : 0;
  const streakStart = last && safeStreak > 0
    ? (() => { const d = new Date(last); d.setDate(d.getDate() - (safeStreak - 1)); return d; })()
    : null;

  for (let i = 0; i < 7; i++) {
    const day = new Date(monday);
    day.setDate(monday.getDate() + i);

    let state = 'missed';
    if (streakStart && day >= streakStart && day <= last) state = 'done';
    if (sameDay(day, now)) state = state === 'done' ? 'done' : 'today';
    if (day > now && !sameDay(day, now)) state = 'future';

    days.push({ state });
  }
  return days;
}
